/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { registerBrick } from 'olympe';
import { ReactBrick, useProperty, getLogger, jsonToSxProps, cssToSxProps, ifNotTransparent, ifNotNull, markdownTextToReactElement } from '@olympeio/core';
import { useOlympeTheme } from './mui';

import React, { useRef, useLayoutEffect } from 'react';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';

import Box from "@mui/material/Box";

/**
 * Provide a Label visual component using MUI Typography
 */
export default class Label extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [hidden] = props.values;
            const labelRef = useRef();
            const allowContentOverflow = useProperty($, 'Allow Content Overflow', true);
            let text = useProperty($, 'Text', true);

            useLayoutEffect(() => {
                if (labelRef?.current) {
                    labelRef.current.parentElement.style.overflow = allowContentOverflow ? "unset" : "hidden";
                }

                if($.get('Auto Compute Height')){
                    // Wait for next frame to let the browser render that new text, so we can get the actual height computed by the browser
                    requestAnimationFrame(() => {
                        $.set('Height', labelRef.current?.clientHeight);
                    });
                }
            }, [labelRef, allowContentOverflow, text]);

            // Change typeof text to string to avoid crash
            if (typeof text !== 'string') {
                if (text) {
                    getLogger('Label').warn('Improper input in text property, got type ' + typeof text + ' instead of string. You might want to use "To String" brick.');
                }
                text = text !== null && text !== undefined ? text.toString() : '';
            }
            const theme = useOlympeTheme($);
            const borderRadius = useProperty($, 'Border Radius', true);
            // Extract text specific SX and CSS properties to be applied on the <p> instead of the box
            const extractFontProps = ({fontFamily, fontSize, lineHeight, letterSpacing, fontWeight, ...otherProps}) => {
                // Remove undefined properties
                const fontProps = {fontFamily, fontSize, lineHeight, letterSpacing, fontWeight};
                Object.keys(fontProps).forEach((k) => fontProps[k] === undefined && delete fontProps[k]);
                // Return separated props series.
                return [fontProps, otherProps];
            };
            const [cssFontProps, cssOtherProps] = extractFontProps(cssToSxProps(useProperty($, 'CSS Property', true)));
            const [sxFontProps, sxOtherProps] = extractFontProps(jsonToSxProps(useProperty($, 'MUI sx [json]', true)));

            const formatText = (text) => {
                try {
                    return markdownTextToReactElement(text, 'span');
                } catch(e) {
                    getLogger('markdownTextToReactElement').error('Improper input in text property, got type ' + typeof text + ' instead of string.', e);
                    return React.createElement('span', {}, 'Format error: invalid input');
                }
            }
            const autoHeight = useProperty($, 'Auto Compute Height', true);
            return !hidden && (
                <ThemeProvider theme={theme}>
                    <Box
                        ref={labelRef}
                        onClick={() => $.trigger('On Click')}
                        sx={{
                            width: 1,
                            ...(autoHeight ? {} : {height: 1}),
                            display: 'flex',
                            overflow: 'visible',
                            borderWidth: useProperty($, 'Border Width', true),
                            alignItems: useProperty($, 'Vertical Align', true),
                            justifyContent: useProperty($, 'Horizontal Align', true),
                            textAlign: useProperty($, 'Horizontal Align', true),
                            borderStyle: 'solid',
                            boxSizing: 'border-box',
                            ...ifNotTransparent('color', useProperty($, 'Text Color', true)),
                            ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color', true)),
                            ...ifNotTransparent('borderColor', useProperty($, 'Border Color', true)),
                            ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                            ...cssOtherProps,
                            ...sxOtherProps
                        }}
                    >
                        <Typography
                            // Properties
                            component={'p'}
                            variant={useProperty($, 'Text Variant', true)}
                            noWrap={useProperty($, 'No Wrap', true)}
                            sx={{
                                // Font family should be overridden by the sx and css props
                                fontFamily: useProperty($, 'Font Family', true),
                                fontSize: useProperty($, 'Font Size', true),
                                fontWeight: useProperty($, 'Bold', true) ? 700 : 400,
                                fontStyle: useProperty($, 'Italic', true) ? 'italic': 'normal',
                                textDecoration: useProperty($, 'Underline', true) ? 'underline': 'none',
                                ...cssFontProps,
                                ...sxFontProps,
                            }}
                        >
                            {useProperty($, 'With Format', true) ? formatText(text) : text}
                        </Typography>
                    </Box>
                </ThemeProvider>
            );
        };
    }
}

registerBrick('01917592266589a76272', Label);
